import { useEffect, useRef } from "react";
import { useAnswerJudge } from "../../hooks/use-answer-judge";
import { useVisibleHeightBelow } from "../../hooks/use-visible-height-below";
import {
  type FingerspellingEntry,
  getFingerspellingImageUrl,
} from "../../lib/fingerspelling";
import type {
  DrillAction,
  DrillSession,
} from "../../lib/fingerspelling-drill-session";
import { CaptionText } from "./CaptionText";

type ReadDrillProps = {
  session: Extract<DrillSession, { status: "playing" }>;
  dispatch: (action: DrillAction) => void;
  onQuit: () => void;
};

/** 正解の「○」を見せてから、次の問題へ進むまでの時間 */
const CORRECT_DISPLAY_MS = 800;
/** ドリルの下にある、ページの余白（`__root.tsx` の main の py-6） */
const PAGE_BOTTOM_PADDING_PX = 24;

/**
 * よむ: 指文字の画像を見て、かなを入力する。
 * 画面の下約4割をキーボードが占めるため、画像・入力欄・結果は、見えている部分に収める。
 * 入力欄は問題が替わっても作り直さず、値も書き換えない（iOS で以降の入力が表示されなくなるため）。
 */
export const ReadDrill = ({ session, dispatch, onQuit }: ReadDrillProps) => {
  const entry = session.questions[session.index];
  if (!entry) return null;
  return (
    <ReadDrillBody
      entry={entry}
      session={session}
      dispatch={dispatch}
      onQuit={onQuit}
    />
  );
};

type ReadDrillBodyProps = ReadDrillProps & { entry: FingerspellingEntry };

const ReadDrillBody = ({
  entry,
  session,
  dispatch,
  onQuit,
}: ReadDrillBodyProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;
  const containerRef = useRef<HTMLDivElement>(null);
  const visibleHeight = useVisibleHeightBelow(containerRef);

  const { answer, inputProps, startQuestion } = useAnswerJudge({
    entry,
    onJudge: (result) => dispatchRef.current({ type: "judge", result }),
  });

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: 問題が替わったときだけ、基準を更新する
  useEffect(() => {
    startQuestion();
  }, [session.index, startQuestion]);

  useEffect(() => {
    if (session.phase !== "correct") return;
    const timer = setTimeout(
      () => dispatchRef.current({ type: "next" }),
      CORRECT_DISPLAY_MS,
    );
    return () => clearTimeout(timer);
  }, [session.phase]);

  // 測れるまで（または visualViewport が使えないとき）は、おおよその高さにする
  const height =
    visibleHeight === null
      ? "calc(100dvh - 4.5rem)"
      : `${visibleHeight - PAGE_BOTTOM_PADDING_PX}px`;

  return (
    <div ref={containerRef} className="flex flex-col gap-2" style={{ height }}>
      <div className="flex items-center justify-between text-sm text-neutral-600">
        <span>
          {session.index + 1} / {session.questions.length}
        </span>
        <button
          type="button"
          onClick={onQuit}
          className="touch-manipulation px-2 py-1"
        >
          ✕ やめる
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 select-none items-center justify-center [-webkit-touch-callout:none]">
        <img
          src={getFingerspellingImageUrl(session.settings.side, entry.id)}
          // よむでは答えのかなを出さない（画像が読めないとき、alt から答えが見えないように）
          alt="指文字"
          draggable={false}
          className="max-h-full max-w-full object-contain"
        />
        {session.phase === "correct" && (
          <span className="absolute inset-0 flex items-center justify-center bg-white/60 text-9xl text-green-600">
            ○
          </span>
        )}
        {session.phase === "incorrect" && (
          <span className="absolute right-2 top-0 text-6xl text-red-600">
            ×
          </span>
        )}
      </div>

      <div className="relative">
        <div className="min-h-12 rounded-xl border-2 border-blue-600 bg-white py-2 text-center text-2xl">
          {answer || (
            <span className="text-base text-neutral-400">かなを入力</span>
          )}
        </div>
        <input
          ref={inputRef}
          aria-label="こたえのかな"
          lang="ja"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          className="absolute inset-0 h-full w-full opacity-0 text-base"
          {...inputProps}
        />
      </div>

      {/* 結果の表示で画面が動かないよう、高さは常に確保しておく */}
      <div className="flex min-h-[5.5rem] flex-col items-center gap-1">
        {session.phase === "incorrect" && (
          <>
            <div className="flex items-center gap-4">
              <span>
                こたえ <b className="text-2xl">{entry.kana}</b>
              </span>
              <button
                type="button"
                // キーボードを閉じないよう、押した瞬間にフォーカスが移るのを止める
                onPointerDown={(event) => event.preventDefault()}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => dispatch({ type: "next" })}
                className="select-none touch-manipulation rounded-xl bg-blue-600 px-5 py-2 font-semibold text-white [-webkit-touch-callout:none]"
              >
                つぎへ
              </button>
            </div>
            <span className="text-sm text-neutral-700">
              <CaptionText segments={entry.caption[session.settings.side]} />
            </span>
          </>
        )}
      </div>
    </div>
  );
};
